import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@vaiu/ui/select";
import { Badge } from "@vaiu/ui/badge";
import { Button } from "@vaiu/ui/button";
import { 
  CalendarDays, 
  TrendingUp, 
  DollarSign, 
  Activity, 
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@vaiu/ui/collapsible";
import {
  extractUserMmMoneyBrandingFromProfile,
  formatAmountForCurrency,
  getDecimalsForCurrencyCode,
  isMmMoneyCurrency,
  isOwnMmMoneyCurrency,
  makeMmMoneyCurrency,
  parseMmMoneyIssuerId,
  resolveAuthUserId,
  summarizeVolumeByCurrency,
  type MmMoneyIssuerView,
} from '@vaiu/utils/mmMoney';
import { roundMonetaryValue } from '@vaiu/utils/currency';
import { MmMoneyIssuerDisplay } from '@vaiu/cash-token-card';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import {
  augmentStatsFromDetailedTransactions,
  countTransactionsForCurrency,
  dashboardHasActivity,
  dedupeTransactions,
  mergeTransactionFallbackIntoStats,
} from '@/utils/dashboardStats';
import type { Transaction } from '@/types/api/payment';

// Interface for user-specific statistics
interface UserStats {
  period: string;
  dateRange: {
    start: Date;
    end: Date;
  };
  overview: {
    totalTransactions: number;
    activeCurrencies: string[];
    totalUsers: number;
    totalVolume: Record<string, number>;
  };
  statistics: {
    created: { count: number; value: Record<string, number>; transactions: any[] };
    redeemed: { count: number; value: Record<string, number>; transactions: any[] };
    expired: { count: number; value: Record<string, number>; transactions: any[] };
    pending: { count: number; value: Record<string, number>; transactions: any[] };
  };
  byCurrency: Record<string, {
    transactions: number;
    volume: number;
    created: number;
    redeemed: number;
  }>;
  tokenTypes?: {
    cash: { count: number; value: Record<string, number> };
    invoice: { count: number; value: Record<string, number> };
  };
}

// Interface for currency panel data
interface CurrencyData {
  currency: string;
  totalAmount: number;
  createdAmount: number;
  redeemedAmount: number;
  expiredAmount: number;
  pendingAmount: number;
  createdTransactions: number;
  redeemedTransactions: number;
  expiredTransactions: number;
  pendingTransactions: number;
}

type TimePeriod = 'today' | 'yesterday' | 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'this-year';

const Dashboard = () => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('this-month');
  const [expandedCurrencies, setExpandedCurrencies] = useState<Set<string>>(new Set());
  const [mmIssuerMeta, setMmIssuerMeta] = useState<Record<string, Partial<MmMoneyIssuerView>>>({});
  const { user } = useAuth();
  const authUserId = resolveAuthUserId(user);
  const { toast } = useToast();

  const fetchMmMoneyMerchantSummary = useCallback(async (issuerUserId: string) => {
    try {
      const p = await platformApi.merchant.getPublicMerchantProfile(issuerUserId);
      return {
        displayName: p.businessName || p.contactName,
        username: (p as { username?: string }).username,
        avatarUrl: p.logo,
        bio: p.description,
      };
    } catch {
      return null;
    }
  }, []);

  const fetchUserStats = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      console.log(`🔍 User Dashboard requesting stats for user: ${user._id}, timePeriod: ${selectedPeriod}`);

      // Scope via Bearer token — do not pass `users` (returns empty aggregates on some backends).
      let data = await platformApi.getStats({
        timePeriod: selectedPeriod,
        includeBalances: true,
        includeBreakdowns: true
      });

      data = augmentStatsFromDetailedTransactions(data) ?? data;

      if ((data?.overview?.totalTransactions ?? 0) === 0) {
        const txResult = await platformApi.getTransactions({
          completedLimit: 200,
          pendingLimit: 200,
        });
        const mergedTx = dedupeTransactions([
          ...(txResult.pending || []),
          ...(txResult.completed || []),
          ...(txResult.transactions || []),
        ] as Transaction[]);
        data = mergeTransactionFallbackIntoStats(data, mergedTx);
      }

      console.log('📊 User stats received:', data);

      setStats(data);
      
      // Set first currency as expanded by default when stats are loaded
      if (data && data.overview.totalVolume && Object.keys(data.overview.totalVolume).length > 0) {
        const sortedCurrencies = getCurrenciesData(data)
          .sort((a, b) => b.totalAmount - a.totalAmount);
        
        if (sortedCurrencies.length > 0) {
          setExpandedCurrencies(new Set([sortedCurrencies[0].currency]));
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
      toast({
        title: "Error",
        description: "Failed to load your token statistics. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserStats();
    }
  }, [user, selectedPeriod]);

  useEffect(() => {
    if (!stats || !user) {
      setMmIssuerMeta({});
      return;
    }

    const keys = [
      ...new Set([
        ...Object.keys(stats.overview.totalVolume || {}),
        ...Object.keys(stats.statistics.created.value || {}),
        ...Object.keys(stats.statistics.redeemed.value || {}),
      ]),
    ].filter(isMmMoneyCurrency);

    const ids = [
      ...new Set(
        keys
          .map((k) => parseMmMoneyIssuerId(k))
          .filter((x): x is string => Boolean(x))
      ),
    ];

    if (ids.length === 0) {
      setMmIssuerMeta({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { merchants } = await platformApi.getPublicMerchantProfilesBatch(ids);
        if (cancelled) return;
        const byId = new Map(merchants.map((m) => [m.id, m]));
        const next: Record<string, Partial<MmMoneyIssuerView>> = {};
        for (const k of keys) {
          const id = parseMmMoneyIssuerId(k);
          if (!id) continue;
          const m = byId.get(id);
          if (m) {
            const biz = (m.businessName || '').trim();
            const contact = (m.contactName || '').trim();
            next[k] = {
              issuerUserId: id,
              displayName: biz || undefined,
              name: contact && contact !== biz ? contact : undefined,
              currencyIconUrl: m.mmMoneyBranding?.currencyIconUrl || m.logo,
            };
          }
        }
        if (authUserId) {
          try {
            const selfKey = makeMmMoneyCurrency(authUserId);
            if (keys.includes(selfKey)) {
              const userMm = extractUserMmMoneyBrandingFromProfile(user);
              const cur = next[selfKey] || { issuerUserId: authUserId };
              next[selfKey] = {
                ...cur,
                issuerUserId: authUserId,
                currencyIconUrl: userMm.currencyIconUrl || cur.currencyIconUrl,
                backgroundUrl: userMm.backgroundImageUrl || cur.backgroundUrl,
              };
            }
          } catch {
            /* ignore invalid issuer id */
          }
        }
        setMmIssuerMeta(next);
      } catch {
        if (!cancelled) setMmIssuerMeta({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stats, user, authUserId]);

  const formatCurrency = (amount: number, currency = 'USD') =>
    formatAmountForCurrency(amount, currency);

  const roundAmount = (amount: number, currency = 'USD'): number =>
    roundMonetaryValue(amount, getDecimalsForCurrencyCode(currency));

  const renderCurrencyLabel = (currencyCode: string) => {
    if (isMmMoneyCurrency(currencyCode)) {
      return (
        <MmMoneyIssuerDisplay
          currencyKey={currencyCode}
          issuer={mmIssuerMeta[currencyCode]}
          fetchMerchantSummary={fetchMmMoneyMerchantSummary}
          tickerOnly={isOwnMmMoneyCurrency(currencyCode, authUserId)}
          className="text-2xl font-bold"
        />
      );
    }
    return currencyCode.toUpperCase();
  };

  // Extract currency data and sort by total volume
  const getCurrenciesData = (statsData: UserStats): CurrencyData[] => {
    const currencies = new Set([
      ...Object.keys(statsData.overview.totalVolume || {}),
      ...Object.keys(statsData.statistics.created.value || {}),
      ...Object.keys(statsData.statistics.redeemed.value || {}),
      ...Object.keys(statsData.statistics.expired.value || {}),
      ...Object.keys(statsData.statistics.pending.value || {})
    ]);

    return Array.from(currencies)
      .filter(currency => {
        const total = statsData.overview.totalVolume[currency] || 0;
        const created = statsData.statistics.created.value[currency] || 0;
        const redeemed = statsData.statistics.redeemed.value[currency] || 0;
        const expired = statsData.statistics.expired.value[currency] || 0;
        const pending = statsData.statistics.pending.value[currency] || 0;
        return total > 0 || created > 0 || redeemed > 0 || expired > 0 || pending > 0;
      })
      .map(currency => {
        // Round all amounts to avoid floating point precision issues
        const totalAmount = roundAmount(statsData.overview.totalVolume[currency] || 0, currency);
        const createdAmount = roundAmount(statsData.statistics.created.value[currency] || 0, currency);
        const redeemedAmount = roundAmount(statsData.statistics.redeemed.value[currency] || 0, currency);
        const expiredAmount = roundAmount(statsData.statistics.expired.value[currency] || 0, currency);
        const pendingAmount = roundAmount(statsData.statistics.pending.value[currency] || 0, currency);

        // Get transaction counts from byCurrency, or count statistics transaction lists
        const currencyStats = statsData.byCurrency[currency] || { transactions: 0, volume: 0, created: 0, redeemed: 0 };
        const createdFromList = countTransactionsForCurrency(
          statsData.statistics.created.transactions,
          currency
        );
        const redeemedFromList = countTransactionsForCurrency(
          statsData.statistics.redeemed.transactions,
          currency
        );

        return {
          currency,
          totalAmount,
          createdAmount,
          redeemedAmount,
          expiredAmount,
          pendingAmount,
          createdTransactions: Math.round(
            currencyStats.created || createdFromList || 0
          ),
          redeemedTransactions: Math.round(
            currencyStats.redeemed || redeemedFromList || 0
          ),
          expiredTransactions: 0, // Will need to calculate from expired data if available
          pendingTransactions: Math.max(0, Math.round(currencyStats.transactions || 0) - Math.round(currencyStats.redeemed || 0) - Math.round(currencyStats.created || 0))
        };
      })
      .sort((a, b) => b.totalAmount - a.totalAmount);
  };

  const toggleCurrency = (currency: string) => {
    const newExpanded = new Set(expandedCurrencies);
    if (newExpanded.has(currency)) {
      newExpanded.delete(currency);
    } else {
      newExpanded.add(currency);
    }
    setExpandedCurrencies(newExpanded);
  };

  const periodLabels = {
    'today': 'Today',
    'yesterday': 'Yesterday',
    'this-week': 'This Week',
    'last-week': 'Last Week',
    'this-month': 'This Month',
    'last-month': 'Last Month',
    'this-year': 'This Year'
  };

  if (!user) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Please log in to view your dashboard</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Time Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-muted-foreground">
            Overview of your token activity for {periodLabels[selectedPeriod].toLowerCase()}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <CalendarDays className="w-4 h-4 text-muted-foreground" />
          <Select value={selectedPeriod} onValueChange={(value: TimePeriod) => setSelectedPeriod(value)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this-week">This Week</SelectItem>
              <SelectItem value="last-week">Last Week</SelectItem>
              <SelectItem value="this-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="this-year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={fetchUserStats} 
            disabled={loading}
            size="sm"
            variant="outline"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Tokens */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.overview.totalTransactions || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats ? summarizeVolumeByCurrency(stats.overview.totalVolume) : '$0.00'} total value
            </p>
          </CardContent>
        </Card>

        {/* Tokens Created */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tokens Created</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats?.statistics.created.count || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats ? summarizeVolumeByCurrency(stats.statistics.created.value) : '$0.00'} created
            </p>
          </CardContent>
        </Card>

        {/* Tokens Redeemed */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tokens Redeemed</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.statistics.redeemed.count || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats ? summarizeVolumeByCurrency(stats.statistics.redeemed.value) : '$0.00'} redeemed
            </p>
          </CardContent>
        </Card>

        {/* Tokens Expired */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tokens Expired</CardTitle>
            <XCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.statistics.expired.count || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats ? summarizeVolumeByCurrency(stats.statistics.expired.value) : '$0.00'} expired
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Currency Panels - Only show if user has token activity */}
      {stats && dashboardHasActivity(stats, getCurrenciesData(stats).length) && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-900">Your Tokens by Currency</h3>
          <p className="text-gray-600">Sorted by total volume (highest first)</p>
          
          {getCurrenciesData(stats).map((currencyData, index) => {
            const isExpanded = expandedCurrencies.has(currencyData.currency);
            
            return (
              <Collapsible
                key={currencyData.currency}
                open={isExpanded}
                onOpenChange={() => toggleCurrency(currencyData.currency)}
              >
                <Card className="overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-500" />
                            )}
                            <CardTitle className="text-2xl font-bold">
                              {renderCurrencyLabel(currencyData.currency)}
                            </CardTitle>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            #{index + 1} by volume
                          </Badge>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">
                            {formatCurrency(currencyData.totalAmount, currencyData.currency)}
                          </div>
                          <div className="text-sm text-gray-600">
                            Total token volume
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <div className="flex items-center space-x-2 mb-2">
                            <TrendingUp className="w-4 h-4 text-blue-600" />
                            <h4 className="font-medium text-blue-900">Created</h4>
                          </div>
                          <p className="text-2xl font-bold text-blue-600">{currencyData.createdTransactions}</p>
                          <p className="text-sm text-blue-700">
                            {formatCurrency(currencyData.createdAmount, currencyData.currency)}
                          </p>
                        </div>
                        
                        <div className="p-4 bg-green-50 rounded-lg">
                          <div className="flex items-center space-x-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <h4 className="font-medium text-green-900">Redeemed</h4>
                          </div>
                          <p className="text-2xl font-bold text-green-600">{currencyData.redeemedTransactions}</p>
                          <p className="text-sm text-green-700">
                            {formatCurrency(currencyData.redeemedAmount, currencyData.currency)}
                          </p>
                        </div>
                        
                        {currencyData.expiredAmount > 0 && (
                          <div className="p-4 bg-orange-50 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <XCircle className="w-4 h-4 text-orange-600" />
                              <h4 className="font-medium text-orange-900">Expired</h4>
                            </div>
                            <p className="text-2xl font-bold text-orange-600">{currencyData.expiredTransactions}</p>
                            <p className="text-sm text-orange-700">
                              {formatCurrency(currencyData.expiredAmount, currencyData.currency)}
                            </p>
                          </div>
                        )}
                        
                        {currencyData.pendingAmount > 0 && (
                          <div className="p-4 bg-sky-50 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <Clock className="w-4 h-4 text-sky-600" />
                              <h4 className="font-medium text-sky-900">Pending</h4>
                            </div>
                            <p className="text-2xl font-bold text-sky-600">{currencyData.pendingTransactions}</p>
                            <p className="text-sm text-sky-700">{formatCurrency(currencyData.pendingAmount, currencyData.currency)}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      )}

      {/* Activity Summary for This Month */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Activity Summary for {periodLabels[selectedPeriod]}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900">Created</h4>
                <p className="text-2xl font-bold text-blue-600">{stats?.statistics.created.count || 0}</p>
                <p className="text-sm text-blue-700">
                  {stats ? summarizeVolumeByCurrency(stats.statistics.created.value) : '$0.00'}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900">Redeemed</h4>
                <p className="text-2xl font-bold text-green-600">{stats?.statistics.redeemed.count || 0}</p>
                <p className="text-sm text-green-700">
                  {stats ? summarizeVolumeByCurrency(stats.statistics.redeemed.value) : '$0.00'}
                </p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-medium text-orange-900">Expired</h4>
                <p className="text-2xl font-bold text-orange-600">{stats?.statistics.expired.count || 0}</p>
                <p className="text-sm text-orange-700">
                  {stats ? summarizeVolumeByCurrency(stats.statistics.expired.value) : '$0.00'}
                </p>
              </div>
            </div>

            {!loading && !dashboardHasActivity(stats, stats ? getCurrenciesData(stats).length : 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No activity found for {periodLabels[selectedPeriod].toLowerCase()}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
